import { Link } from "react-router-dom";
import { useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import LabelArt from "@/components/LabelArt";
import { useCart } from "@/context/CartContext";

const CartPage = () => {
  const { items, productsBySlug, setQty, remove, subtotal, checkout } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try { await checkout(); } catch { setLoading(false); }
  };

  return (
    <div data-testid="cart-page" className="bg-ivory min-h-[70vh]">
      <section className="border-b border-line py-14 md:py-20 text-center">
        <Reveal>
          <p className="eyebrow">Your Bag</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight">Shopping Cart</h1>
        </Reveal>
      </section>
      <section className="mx-auto max-w-5xl px-6 sm:px-12 py-14">
        {items.length === 0 ? (
          <Reveal className="py-16 text-center">
            <p className="font-serif text-2xl font-light italic text-stone2">Your bag is empty — the candles are waiting.</p>
            <Link to="/shop" data-testid="cart-page-shop-link" className="btn-primary mt-8">Shop Candles</Link>
          </Reveal>
        ) : (
          <Reveal>
            <div className="hidden grid-cols-12 gap-4 border-b border-line pb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 md:grid">
              <span className="col-span-6">Candle</span><span className="col-span-2 text-center">Quantity</span><span className="col-span-2 text-right">Price</span><span className="col-span-2 text-right">Total</span>
            </div>
            <ul>
              {items.map((item) => {
                const p = productsBySlug[item.slug];
                if (!p) return null;
                return (
                  <li key={item.slug} data-testid={`cart-page-item-${item.slug}`} className="grid grid-cols-12 items-center gap-4 border-b border-line py-6">
                    <div className="col-span-10 md:col-span-6 flex items-center gap-5">
                      <div className="h-24 w-20 shrink-0 overflow-hidden border border-line bg-white">
                        {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <LabelArt name={p.name} size={p.size} compact />}
                      </div>
                      <div>
                        <Link to={`/product/${p.slug}`} className="font-serif text-xl font-light hover:italic transition-all">{p.name}</Link>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone2">{p.scent} · Soy Wax · {p.size}</p>
                        <button data-testid={`cart-page-remove-${item.slug}`} onClick={() => remove(item.slug)} className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-stone2 hover:text-ink transition-colors"><X size={11} /> Remove</button>
                      </div>
                    </div>
                    <div className="col-span-4 md:col-span-2 flex md:justify-center">
                      <div className="flex items-center border border-line bg-white">
                        <button data-testid={`cart-page-minus-${item.slug}`} onClick={() => setQty(item.slug, item.qty - 1)} className="px-3 py-2 hover:bg-cream transition-colors"><Minus size={12} /></button>
                        <span className="w-8 text-center text-xs" data-testid={`cart-page-qty-${item.slug}`}>{item.qty}</span>
                        <button data-testid={`cart-page-plus-${item.slug}`} onClick={() => setQty(item.slug, item.qty + 1)} className="px-3 py-2 hover:bg-cream transition-colors"><Plus size={12} /></button>
                      </div>
                    </div>
                    <p className="col-span-4 md:col-span-2 text-right text-sm text-stone2">${p.price.toFixed(2)}</p>
                    <p className="col-span-4 md:col-span-2 text-right text-sm font-medium">${(p.price * item.qty).toFixed(2)}</p>
                  </li>
                );
              })}
            </ul>
            <div className="mt-10 flex flex-col items-end gap-6">
              <div className="w-full max-w-sm border border-line bg-white p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Subtotal</span>
                  <span data-testid="cart-page-subtotal" className="font-serif text-3xl font-light">${subtotal.toFixed(2)}</span>
                </div>
                {subtotal >= 75 ? (
                  <p data-testid="free-shipping-unlocked" className="mt-3 text-xs leading-relaxed text-ink">Free shipping unlocked — your order ships on us. Taxes, promo codes and gift cards are applied at our secure checkout.</p>
                ) : (
                  <div className="mt-3" data-testid="free-shipping-progress">
                    <p className="text-xs leading-relaxed text-stone2">You're <span className="text-ink font-medium">${(75 - subtotal).toFixed(2)}</span> away from free shipping</p>
                    <div className="mt-2 h-[3px] w-full bg-line overflow-hidden">
                      <div className="h-full bg-ink transition-all duration-700 ease-out" style={{ width: `${Math.min(100, (subtotal / 75) * 100)}%` }} />
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-stone2">Flat $6.95 standard shipping, taxes, promo codes and gift cards are applied at our secure checkout.</p>
                  </div>
                )}
                <button data-testid="cart-page-checkout-button" onClick={handleCheckout} disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-50">
                  {loading ? "Preparing Checkout…" : "Checkout"}
                </button>
              </div>
              <Link to="/shop" data-testid="cart-page-continue-shopping" className="btn-outline">Continue Shopping</Link>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
};

export default CartPage;
